import React, { type MutableRefObject, useCallback } from 'react';
import { Form } from 'antd';
import type { Rule } from 'antd/es/form';
import { useIntl } from 'react-intl';

import { ChargeSummaryAmountField } from '../charge-summary-amount-field';
import { roundCurrency2 } from '../../utils/payment-math';
import {
  syncPeerSplitAmountOnly,
  syncSplitAmountsFromEditedField,
} from '../../utils/split-amount-sync';
import { MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD } from '../../constants';

function getPeerSplitFullFieldName(
  fieldName: 'splitAmount1' | 'splitAmount2',
): 'splitAmount1' | 'splitAmount2' {
  return fieldName === 'splitAmount1' ? 'splitAmount2' : 'splitAmount1';
}

export interface SplitPayInFullChargeFieldProps {
  certificateTotal: number;
  fieldName: 'splitAmount1' | 'splitAmount2';
  editedKey: '1' | '2';
  rules: Rule[];
  formItemClassName: string;
  splitFullLastRef: MutableRefObject<'1' | '2'>;
}

export function SplitPayInFullChargeField({
  certificateTotal,
  fieldName,
  editedKey,
  rules,
  formItemClassName,
  splitFullLastRef,
}: SplitPayInFullChargeFieldProps): React.ReactElement {
  const intl = useIntl();
  const form = Form.useFormInstance();

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      syncPeerSplitAmountOnly({
        total: certificateTotal,
        editedField: editedKey,
        rawValue: e.target.value,
        setFieldsValue: (patch) => form.setFieldsValue(patch),
        lastEditedRef: splitFullLastRef,
      });
    },
    [certificateTotal, editedKey, form, splitFullLastRef],
  );

  const handleAmountBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      syncSplitAmountsFromEditedField({
        total: certificateTotal,
        editedField: editedKey,
        rawValue: e.target.value,
        setFieldsValue: (patch) => form.setFieldsValue(patch),
        lastEditedRef: splitFullLastRef,
      });
      const peerFieldName = getPeerSplitFullFieldName(fieldName);
      void form.validateFields([fieldName, peerFieldName]).catch(() => undefined);
    },
    [certificateTotal, editedKey, fieldName, form, splitFullLastRef],
  );

  return (
    <ChargeSummaryAmountField
      name={fieldName}
      rules={rules}
      dependencies={['splitAmount1', 'splitAmount2', 'paymentPlan', 'splitPayInFull']}
      formItemClassName={formItemClassName}
      ariaLabel={intl.formatMessage({ id: 'certificates.payment.chargeAmountAria' })}
      stepMax={roundCurrency2(certificateTotal)}
      stepMin={MIN_MULTICARD_SPLIT_CARD_AMOUNT_USD}
      onChange={handleAmountChange}
      onBlur={handleAmountBlur}
    />
  );
}
